/** Horizontal tab bar (controlled). tabs: [{key,label}] */
import { useEffect, useMemo, useRef, useState } from "react";
import { CaretLeft, CaretRight, CaretUp, CaretDown, Sliders, MagnifyingGlass } from "@phosphor-icons/react";

export function TabNav({ tabs, active, onChange, testid = "tabnav" }) {
  return (
    <div className="border-b border-[var(--border)] bg-[var(--surface)] px-4 sm:px-8 overflow-x-auto no-scrollbar" data-testid={testid}>
      <div className="flex gap-1 min-w-max">
        {tabs.map((t) => {
          const on = t.key === active;
          return (
            <button
              key={t.key}
              data-testid={`tab-${t.key}`}
              onClick={() => onChange(t.key)}
              className={`relative px-4 py-3 text-sm font-semibold whitespace-nowrap transition-colors ${on ? "text-[var(--text-1)]" : "text-[var(--text-3)] hover:text-[var(--text-2)]"}`}
            >
              {t.label}
              {on && <span className="absolute left-2 right-2 -bottom-px h-0.5 bg-[var(--text-1)] rounded-full" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Master-record verification badge: active vs pending manager approval. */
export function StatusBadge({ status, by }) {
  const pending = status === "pending";
  return (
    <span
      title={pending && by ? `Added by ${by} — awaiting approval` : undefined}
      className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm ${pending ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${pending ? "bg-amber-500" : "bg-emerald-500"}`} />
      {pending ? "Pending" : "Active"}
    </span>
  );
}

export function PageHeader({ overline, title, children }) {
  return (
    <div className="px-4 sm:px-8 pt-5 sm:pt-8 pb-4 sm:pb-6 border-b border-[var(--border)] bg-[var(--surface)] flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
      <div>
        {overline && <div className="overline mb-2">{overline}</div>}
        <h1 className="heading text-2xl sm:text-4xl font-black tracking-tight">{title}</h1>
      </div>
      <div className="flex items-center gap-2 flex-wrap">{children}</div>
    </div>
  );
}

export function Section({ children, className = "" }) {
  return <div className={`p-4 sm:p-8 ${className}`}>{children}</div>;
}

export function Btn({ variant = "primary", className = "", ...props }) {
  const styles = {
    primary: "bg-[var(--text-1)] text-white hover:bg-zinc-800",
    outline: "bg-white border border-[var(--border)] text-[var(--text-1)] hover:bg-zinc-50",
    accent: "bg-[var(--accent)] text-white hover:bg-[var(--accent-hover)]",
    danger: "bg-white border border-red-200 text-red-600 hover:bg-red-50",
    ghost: "bg-transparent text-[var(--text-2)] hover:bg-zinc-100",
  }[variant];
  return (
    <button
      className={`px-4 py-2 rounded-sm text-sm font-semibold transition-colors duration-150 ${styles} ${className}`}
      {...props}
    />
  );
}

export function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-wider text-[var(--text-2)]">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

export function Input(props) {
  return (
    <input
      {...props}
      className={`w-full px-3 py-2 border border-[var(--border)] rounded-sm bg-white focus:ring-2 focus:ring-[var(--accent)] focus:outline-none text-sm ${props.className || ""}`}
    />
  );
}

export function Select(props) {
  return (
    <select
      {...props}
      className={`w-full px-3 py-2 border border-[var(--border)] rounded-sm bg-white focus:ring-2 focus:ring-[var(--accent)] focus:outline-none text-sm ${props.className || ""}`}
    />
  );
}

/**
 * Data table with universal controls (applies to every table using this component):
 *  - click a column header to sort (asc → desc → off), numeric-aware
 *  - quick filter box matching any visible cell text
 *  - client pagination: 15 rows per page with Prev / Next
 *  - first VISIBLE column freezes (sticky left) while scrolling horizontally
 *  - always-visible styled horizontal scrollbar + scroll arrow buttons
 *  - column show/hide chooser (persisted per page in localStorage)
 */
const cellText = (node) => {
  if (node == null || node === false || node === true) return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(cellText).join(" ");
  if (node.props !== undefined) return cellText(node.props.children);
  return "";
};

const sortVal = (node) => {
  const t = cellText(node).trim();
  const cleaned = t.replace(/[₹,\s]/g, "").replace(/%$/, "");
  if (cleaned !== "" && /^[-+]?\d*\.?\d+([eE][-+]?\d+)?$/.test(cleaned)) return parseFloat(cleaned);
  return t.toLowerCase();
};

const VIRTUAL_OVERSCAN = 8;

/** Data table. `virtual` renders ONLY the rows in view inside a fixed-height scroller
 *  (no pagination) so lists of thousands of rows stay smooth; filter/sort still apply. */
export function Table({ headers, rows, empty = "No data", tableId, pageSize = 15, filterable = true, sortable = true, virtual = false, maxHeight = "65vh", "data-testid": dataTestid }) {
  const scrollRef = useRef(null);
  const tbodyRef = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewH, setViewH] = useState(600);
  const [rowH, setRowH] = useState(44);
  const [ovf, setOvf] = useState({ any: false, left: false, right: false });
  const [chooser, setChooser] = useState(false);
  const [filter, setFilter] = useState("");
  const [sort, setSort] = useState(null); // { col, dir: 1 | -1 }
  const [page, setPage] = useState(0);
  const storageKey = useMemo(
    () => `cols:${window.location.pathname}:${tableId || headers.map((h) => (typeof h === "string" ? h : "·")).join("|")}`,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tableId, headers.length]
  );
  const [hidden, setHidden] = useState([]);
  useEffect(() => {
    try { setHidden(JSON.parse(localStorage.getItem(storageKey)) || []); }
    catch (err) { console.debug("Table column prefs unreadable, resetting:", err?.message); setHidden([]); }
  }, [storageKey]);

  const visIdx = headers.map((_, i) => i).filter((i) => !hidden.includes(i));
  const firstVis = visIdx[0];

  // ---- filter → sort → paginate (operates on row arrays, JSX preserved) ----
  const q = filter.trim().toLowerCase();
  let display = q
    ? rows.filter((r) => visIdx.some((ci) => cellText(r[ci]).toLowerCase().includes(q)))
    : rows;
  if (sort && sort.col != null) {
    display = [...display].sort((a, b) => {
      const x = sortVal(a[sort.col]), y = sortVal(b[sort.col]);
      if (typeof x === "number" && typeof y === "number") return sort.dir * (x - y);
      if (typeof x === "number") return -sort.dir;
      if (typeof y === "number") return sort.dir;
      return sort.dir * String(x).localeCompare(String(y));
    });
  }
  const totalRows = display.length;
  const totalPages = virtual ? 1 : Math.max(1, Math.ceil(totalRows / pageSize));
  const safePage = virtual ? 0 : Math.min(page, totalPages - 1);
  // Virtual window: rows [vStart, vEnd) are rendered; spacer rows stand in for the rest.
  const vStart = virtual ? Math.max(0, Math.floor(scrollTop / rowH) - VIRTUAL_OVERSCAN) : 0;
  const vEnd = virtual ? Math.min(totalRows, Math.ceil((scrollTop + viewH) / rowH) + VIRTUAL_OVERSCAN) : 0;
  const paged = virtual ? display.slice(vStart, vEnd) : display.slice(safePage * pageSize, safePage * pageSize + pageSize);
  const topPad = virtual ? vStart * rowH : 0;
  const bottomPad = virtual ? Math.max(0, totalRows - vEnd) * rowH : 0;

  // Virtual mode: track the scroller's vertical position/size and calibrate the row height
  // from what is actually rendered (so tall multi-line rows don't drift the scrollbar).
  useEffect(() => {
    if (!virtual) return;
    const el = scrollRef.current;
    if (!el) return;
    let raf = 0;
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(() => { raf = 0; setScrollTop(el.scrollTop); }); };
    const onSize = () => setViewH(el.clientHeight || 600);
    onSize();
    el.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onSize);
    return () => { el.removeEventListener("scroll", onScroll); window.removeEventListener("resize", onSize); if (raf) cancelAnimationFrame(raf); };
  }, [virtual]);
  useEffect(() => {
    if (!virtual || !tbodyRef.current) return;
    const trs = Array.from(tbodyRef.current.querySelectorAll("tr[data-vrow]"));
    if (trs.length < 2) return;
    const h = (trs[trs.length - 1].getBoundingClientRect().bottom - trs[0].getBoundingClientRect().top) / trs.length;
    if (h > 10 && Math.abs(h - rowH) > 1) setRowH(h);
  }, [virtual, paged.length, rowH, vStart]);
  useEffect(() => { if (virtual && scrollRef.current) { scrollRef.current.scrollTop = 0; setScrollTop(0); } }, [virtual, q, sort, rows.length]);

  // Reset to page 1 when the underlying data or the filter changes.
  useEffect(() => { setPage(0); }, [q, rows.length]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => setOvf({
      any: el.scrollWidth > el.clientWidth + 2,
      left: el.scrollLeft > 2,
      right: el.scrollLeft + el.clientWidth < el.scrollWidth - 2,
    });
    update();
    el.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => { el.removeEventListener("scroll", update); window.removeEventListener("resize", update); };
  }, [rows, hidden, safePage, q]);

  const toggleCol = (i) => {
    setHidden((prev) => {
      const next = prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i];
      if (headers.length - next.length < 2) return prev; // keep at least 2 columns
      try { localStorage.setItem(storageKey, JSON.stringify(next)); }
      catch (err) { console.debug("Could not persist column prefs:", err?.message); }
      return next;
    });
  };
  const scrollBy = (dx) => scrollRef.current?.scrollBy({ left: dx, behavior: "smooth" });
  const colLabel = (h, i) => (typeof h === "string" && h.trim()) ? h : (i === headers.length - 1 ? "Actions" : `Column ${i + 1}`);
  const stickyCell = (i, isHeader) => {
    const col = (ovf.any && i === firstVis)
      ? `sticky left-0 ${isHeader ? "bg-zinc-50" : "bg-white group-hover:bg-zinc-50"}${ovf.left ? " table-sticky-shadow" : ""}`
      : "";
    if (isHeader && virtual) return `${col} sticky top-0 ${col ? "z-[3]" : "z-[2] bg-zinc-50"}`;
    return col ? `${col} z-[1]` : "";
  };
  const isSortableCol = (i) => sortable && typeof headers[i] === "string" && headers[i].trim() !== "";
  const onHeadClick = (i) => {
    if (!isSortableCol(i)) return;
    setSort((s) => (!s || s.col !== i) ? { col: i, dir: 1 } : (s.dir === 1 ? { col: i, dir: -1 } : null));
  };

  const showFilter = filterable && rows.length > 0;
  const showToolbar = showFilter || ovf.any || hidden.length > 0;
  return (
    <div data-testid={dataTestid}>
      {showToolbar && (
        <div className="flex items-center justify-between gap-1.5 mb-1.5 relative" data-testid="table-toolbar">
          <div className="flex items-center gap-2">
            {showFilter && (
              <div className="relative">
                <MagnifyingGlass size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--text-3)]" />
                <input
                  data-testid="table-filter-input"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  placeholder="Filter rows…"
                  className="pl-7 pr-2 py-1.5 w-44 sm:w-56 text-xs border border-[var(--border)] rounded-sm bg-white focus:ring-2 focus:ring-[var(--accent)] focus:outline-none"
                />
              </div>
            )}
            {q && (
              <span className="text-[11px] text-[var(--text-3)]" data-testid="table-filter-count">
                {totalRows} match{totalRows === 1 ? "" : "es"}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            {hidden.length > 0 && (
              <span className="text-[11px] text-[var(--text-3)] mr-1">{hidden.length} column{hidden.length > 1 ? "s" : ""} hidden</span>
            )}
            {ovf.any && (
              <>
                <button type="button" data-testid="table-scroll-left" onClick={() => scrollBy(-320)} disabled={!ovf.left}
                  className="p-1.5 rounded-sm border border-[var(--border)] bg-white text-[var(--text-2)] disabled:opacity-30 hover:bg-zinc-50" title="Scroll left">
                  <CaretLeft size={13} weight="bold" />
                </button>
                <button type="button" data-testid="table-scroll-right" onClick={() => scrollBy(320)} disabled={!ovf.right}
                  className="p-1.5 rounded-sm border border-[var(--border)] bg-white text-[var(--text-2)] disabled:opacity-30 hover:bg-zinc-50" title="Scroll right">
                  <CaretRight size={13} weight="bold" />
                </button>
              </>
            )}
            <button type="button" data-testid="table-columns-btn" onClick={() => setChooser((v) => !v)}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm border text-[11px] font-semibold ${chooser ? "border-[var(--text-1)] bg-[var(--text-1)] text-white" : "border-[var(--border)] bg-white text-[var(--text-2)] hover:bg-zinc-50"}`}>
              <Sliders size={13} weight="bold" /> Columns
            </button>
            {chooser && (
              <>
                <div className="fixed inset-0 z-[19]" onClick={() => setChooser(false)} />
                <div className="absolute right-0 top-full mt-1 z-20 bg-white border border-[var(--border)] rounded-sm shadow-lg p-2 w-52 max-h-72 overflow-y-auto" data-testid="table-columns-menu">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-[var(--text-3)] px-1.5 pb-1.5">Show / hide columns</div>
                  {headers.map((h, i) => (
                    <label key={i} className="flex items-center gap-2 px-1.5 py-1 rounded-sm hover:bg-zinc-50 cursor-pointer select-none">
                      <input type="checkbox" data-testid={`column-toggle-${i}`} className="w-3.5 h-3.5 accent-[var(--accent)]"
                        checked={!hidden.includes(i)} onChange={() => toggleCol(i)} />
                      <span className="text-xs">{colLabel(h, i)}</span>
                    </label>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}
      <div className={`card-flat overflow-x-auto table-scroll${virtual ? " overflow-y-auto" : ""}`} ref={scrollRef}
        style={virtual ? { maxHeight } : undefined} data-testid={virtual ? "table-virtual-scroller" : undefined}>
        <table className="w-full text-sm min-w-max">
          <thead>
            <tr className="bg-zinc-50 border-b border-[var(--border)]">
              {visIdx.map((i) => {
                const active = sort && sort.col === i;
                return (
                  <th
                    key={`${headers[i] || "col"}-${i}`}
                    onClick={() => onHeadClick(i)}
                    data-testid={`table-sort-${i}`}
                    title={isSortableCol(i) ? "Click to sort" : undefined}
                    className={`text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-[var(--text-2)] ${isSortableCol(i) ? "cursor-pointer select-none hover:text-[var(--text-1)]" : ""} ${stickyCell(i, true)}`}
                  >
                    <span className="inline-flex items-center gap-1">
                      {headers[i]}
                      {active && (sort.dir === 1 ? <CaretUp size={11} weight="bold" /> : <CaretDown size={11} weight="bold" />)}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody ref={tbodyRef}>
            {/* Note: rows/cells are keyed by POSITION deliberately — Table receives
                plain arrays (no stable ids) and column index IS the column identity. */}
            {virtual && topPad > 0 && <tr aria-hidden="true" data-testid="table-virtual-top"><td colSpan={visIdx.length} style={{ height: topPad, padding: 0, border: 0 }} /></tr>}
            {paged.length === 0 ? (
              <tr><td colSpan={visIdx.length} className="text-center py-12 text-[var(--text-3)]">{q ? "No rows match the filter" : empty}</td></tr>
            ) : paged.map((r, ri) => (
              <tr key={virtual ? vStart + ri : ri} data-vrow={virtual ? vStart + ri : undefined} className="group border-b border-[var(--border)] last:border-0 hover:bg-zinc-50 transition-colors">
                {visIdx.map((i) => <td key={i} className={`px-4 py-3 align-middle ${stickyCell(i, false)}`}>{r[i]}</td>)}
              </tr>
            ))}
            {virtual && bottomPad > 0 && <tr aria-hidden="true" data-testid="table-virtual-bottom"><td colSpan={visIdx.length} style={{ height: bottomPad, padding: 0, border: 0 }} /></tr>}
          </tbody>
        </table>
      </div>
      {virtual && totalRows > 0 && (
        <div className="flex items-center justify-end mt-2 text-[11px] text-[var(--text-3)] mono" data-testid="table-virtual-info">
          {totalRows.toLocaleString()} row{totalRows === 1 ? "" : "s"} · scroll to see all
        </div>
      )}
      {!virtual && totalRows > pageSize && (
        <div className="flex items-center justify-end gap-2 mt-2" data-testid="table-pagination">
          <span className="text-[11px] text-[var(--text-3)] mono" data-testid="table-page-info">
            {safePage * pageSize + 1}–{Math.min((safePage + 1) * pageSize, totalRows)} of {totalRows}
          </span>
          <button type="button" data-testid="table-page-prev" disabled={safePage === 0}
            onClick={() => setPage(Math.max(0, safePage - 1))}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-sm border border-[var(--border)] bg-white text-[11px] font-semibold text-[var(--text-2)] disabled:opacity-30 hover:bg-zinc-50">
            <CaretLeft size={11} weight="bold" /> Prev
          </button>
          <span className="text-[11px] mono text-[var(--text-2)]">{safePage + 1} / {totalPages}</span>
          <button type="button" data-testid="table-page-next" disabled={safePage >= totalPages - 1}
            onClick={() => setPage(Math.min(totalPages - 1, safePage + 1))}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-sm border border-[var(--border)] bg-white text-[11px] font-semibold text-[var(--text-2)] disabled:opacity-30 hover:bg-zinc-50">
            Next <CaretRight size={11} weight="bold" />
          </button>
        </div>
      )}
    </div>
  );
}

/** Shimmer skeleton block. */
export function Skeleton({ className = "" }) {
  return <div className={`animate-pulse rounded-sm bg-zinc-200/70 ${className}`} />;
}

/** Placeholder rows while a table loads. */
export function SkeletonTable({ cols = 5, rows = 6 }) {
  return (
    <div className="card-flat overflow-hidden" data-testid="skeleton-table">
      <div className="bg-zinc-50 border-b border-[var(--border)] flex gap-4 px-4 py-3">
        {Array.from({ length: cols }).map((_, i) => <Skeleton key={i} className="h-3 flex-1" />)}
      </div>
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex gap-4 px-4 py-4 border-b border-[var(--border)] last:border-0">
          {Array.from({ length: cols }).map((_, i) => <Skeleton key={i} className="h-4 flex-1" />)}
        </div>
      ))}
    </div>
  );
}

/** Friendly empty state with an optional call to action. */
export function EmptyState({ icon: Icon, title, hint, action }) {
  return (
    <div className="card-flat py-14 px-6 flex flex-col items-center text-center" data-testid="empty-state">
      {Icon && <div className="w-12 h-12 rounded-full bg-zinc-100 flex items-center justify-center mb-4 text-[var(--text-3)]"><Icon size={24} weight="bold" /></div>}
      <div className="heading font-bold text-lg text-[var(--text-1)]">{title}</div>
      {hint && <div className="text-sm text-[var(--text-3)] mt-1 max-w-sm">{hint}</div>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function KPI({ label, value, sub, tone = "default", testid }) {
  const tones = {
    default: "text-[var(--text-1)]",
    pending: "text-[var(--pending)]",
    success: "text-[var(--success)]",
    warn: "text-[var(--warn)]",
    critical: "text-[var(--critical)]",
    accent: "text-[var(--accent)]",
  };
  return (
    <div className="card-flat p-5" data-testid={testid}>
      <div className="overline">{label}</div>
      <div className={`kpi-num mt-3 mono ${tones[tone]}`}>{value}</div>
      {sub && <div className="text-xs text-[var(--text-3)] mt-1">{sub}</div>}
    </div>
  );
}
